import { useEffect, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';

/**
 * Generic "confirm + edit recipient" dialog used for every transactional
 * email touchpoint that fires from a user button (cotización,
 * certificado de garantía, etc.). Pre-fills the recipient with whatever
 * the contacto has on file; admin can edit before submitting.
 *
 * BCC / subject / body all live server-side — this dialog stays
 * intentionally thin so adding a new touchpoint only needs a new server
 * email module + an `onSubmit` here.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function EmailConfirmDialog({
  open,
  title,
  description,
  defaultEmail,
  onCancel,
  onSubmit,
  submitLabel = 'Enviar',
}: {
  open: boolean;
  title: string;
  /** Short caption under the title — e.g. "Se enviará a Juan con el PDF adjunto." */
  description?: string;
  /** Pre-filled recipient — usually `contacto.Mail_C`. */
  defaultEmail: string;
  onCancel: () => void;
  /** Resolves once the send completes (success or handled failure). The
   *  dialog stays open while the promise is pending. */
  onSubmit: (recipientEmail: string) => Promise<void>;
  submitLabel?: string;
}) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setEmail(defaultEmail.trim());
  }, [open, defaultEmail]);

  const trimmed = email.trim();
  const valid = EMAIL_RE.test(trimmed);

  const handleConfirm = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Mail className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <DialogTitle>{title}</DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
            </div>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="email-confirm-recipient" className="text-sm">
              Destinatario
            </Label>
            <Input
              id="email-confirm-recipient"
              type="email"
              autoComplete="email"
              variant="filled"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ejemplo@dominio.com"
              disabled={submitting}
            />
            {!valid && trimmed.length > 0 && (
              <p className="text-xs text-destructive">
                Ingresá un correo electrónico válido.
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!valid || submitting}
            className="gap-2"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Mail className="h-4 w-4" aria-hidden />
            )}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
